/**
 * LanguageFallbackIndicator - Shows when content is displayed in fallback language
 * Displays a subtle banner when the current page content is shown in English
 * instead of the user's selected language.
 * @module components/ui/LanguageFallbackIndicator
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Languages, RefreshCw } from 'lucide-react';
import { DEFAULT_LANGUAGE, LANGUAGE_CONFIG } from '../../i18n';
import { getTranslationSource } from '../../utils/clientTranslation';

const LanguageFallbackIndicator = ({ className = '' }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || DEFAULT_LANGUAGE;

  // Don't show if user is already viewing in English (default language)
  if (currentLang === DEFAULT_LANGUAGE) {
    return null;
  }

  const currentConfig = LANGUAGE_CONFIG[currentLang];
  const englishConfig = LANGUAGE_CONFIG[DEFAULT_LANGUAGE];
  const translationInfo = getTranslationSource(currentLang);

  // If translations are available, don't show the indicator
  if (translationInfo.available && translationInfo.source !== 'fallback') {
    return null;
  }

  return (
    <div className={`bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 ${className}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <Languages className="h-5 w-5 text-amber-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <p className="text-sm font-medium text-amber-800">
              Content displayed in English
            </p>
          </div>
          <p className="text-sm text-amber-700">
            This page content is displayed in{' '}
            <span className="font-medium">
              {englishConfig?.label || 'English'}
            </span>
            {' '}because translations to{' '}
            <span className="font-medium">
              {currentConfig?.label || currentLang.toUpperCase()}
            </span>
            {' '}are not yet available.
          </p>
          <div className="flex items-center gap-4 mt-2">
            <p className="text-xs text-amber-600">
              Automatic translation is being configured.{' '}
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-1 underline hover:no-underline font-medium"
              >
                <RefreshCw className="h-3 w-3" />
                Check for updates
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageFallbackIndicator;